import React from "react";
import { MapPin, Crosshair, Loader2, RefreshCw } from "lucide-react";
import { useLocation } from "@/context/LocationContext";
import { toast } from "sonner";

/**
 * LocationChip — the universal freshness badge for Slice 1.
 *
 * Shows *what* location is being used AND *how fresh* it is, so users never
 * confuse a 15-minute-old GPS reading with a live one.
 *
 * States (from LocationContext.state):
 *   live       → green pulsing dot, "Live · <city>"        · Crosshair icon
 *   recent     → green solid  dot, "<city> · <age>"        · Crosshair
 *   manual     → blue         dot, "<city> · picked"       · MapPin
 *   last_known → amber        dot, "<city> · <age>"        · MapPin
 *   cached     → orange       dot, "<city> · <age>"        · MapPin
 *   default    → grey         dot, "<city> · default"      · MapPin
 *
 * Tapping refreshes via LocationContext.detect() when possible.
 *
 * Props:
 *   compact   - shorter pill (mobile chrome).
 *   showAge   - default true; hide the "· 5m ago" suffix for tight spots.
 *   className - extra styles.
 */
const STATE_CONFIG = {
  live:       { dot: "bg-emerald-500 animate-pulse", ring: "ring-emerald-200",
                text: "text-emerald-800",   Icon: Crosshair, label: "Live"        },
  recent:     { dot: "bg-emerald-500",               ring: "ring-emerald-100",
                text: "text-emerald-800",   Icon: Crosshair, label: "Recent"      },
  manual:     { dot: "bg-sky-500",                   ring: "ring-sky-100",
                text: "text-sky-800",       Icon: MapPin,    label: "Picked"      },
  last_known: { dot: "bg-amber-500",                 ring: "ring-amber-100",
                text: "text-amber-800",     Icon: MapPin,    label: "Last known"  },
  cached:     { dot: "bg-orange-500",                ring: "ring-orange-100",
                text: "text-orange-800",    Icon: MapPin,    label: "Cached"      },
  default:    { dot: "bg-stone-400",                 ring: "ring-stone-100",
                text: "text-stone-600",     Icon: MapPin,    label: "Default"     },
};

export default function LocationChip({
  compact = false,
  showAge = true,
  className = "",
  onClickOverride = null,   // e.g. open a city-picker modal instead of geo-detect
}) {
  const {
    loc, state, age_label, loading, permission, detect,
  } = useLocation() || {};

  const cfg = STATE_CONFIG[state] || STATE_CONFIG.default;
  const Icon = loading ? Loader2 : cfg.Icon;

  const onClick = async (e) => {
    e?.stopPropagation?.();
    if (onClickOverride) return onClickOverride(e);
    const r = await detect?.();
    if (r) toast.success(`Located · ${r.city}`);
    else if (permission === "denied")
      toast.error("Location permission blocked in your browser settings.");
    else toast.error("Could not fetch your location");
  };

  const suffix = showAge
    ? (state === "live" ? "Live"
      : state === "manual" ? "picked"
      : state === "default" ? "default"
      : age_label || "")
    : "";

  const size = compact ? "text-[11px] px-2 py-1" : "text-xs px-2.5 py-1.5";
  const iconSize = compact ? 12 : 13;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border border-stone-200 bg-white hover:border-stone-300 ring-1 ${cfg.ring} ${cfg.text} ${size} ${className}`}
      data-testid="location-chip"
      data-location-state={state}
      title={
        state === "live"       ? "Live GPS fix. Tap to refresh."
      : state === "recent"     ? `GPS fix ${age_label}. Tap to refresh.`
      : state === "manual"     ? "You picked this location. Tap to detect."
      : state === "last_known" ? `Last known GPS ${age_label}. Tap to refresh.`
      : state === "cached"     ? `Very old GPS (${age_label}). Tap to refresh.`
                               : "Default location. Tap to detect your city."
      }
    >
      <Icon size={iconSize} className={loading ? "animate-spin" : ""} />
      <span className="truncate max-w-[120px]" data-testid="location-chip-city">
        {loc?.city || "Set location"}
      </span>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {suffix && !compact && (
        <span className="opacity-70 font-medium" data-testid="location-chip-age">
          · {suffix}
        </span>
      )}
      {(state === "cached" || state === "last_known" || state === "default") && !loading && !compact && (
        <RefreshCw size={11} className="opacity-60 ml-0.5" />
      )}
    </button>
  );
}

/* Compact preset for tight headers / navbar edges. */
export function LocationChipCompact(props) {
  return <LocationChip compact showAge={false} {...props} />;
}
